'use client';

import { track } from '@/utils/rudderstack';
import { LDContext, LDFlagSet } from 'launchdarkly-react-client-sdk';

declare global {
  interface Window {
    le_flags?: Record<string, any>;
  }
}

// Exported for testing
export function storageAvailable() {
  try {
    // Test if we can use sessionStorage
    sessionStorage.setItem('le_storage_test', 'test');
    sessionStorage.removeItem('le_storage_test');
    return true;
  } catch (e) {
    return false;
  }
}

function storeFlagsJson(flagsJson: string) {
  try {
    // Test if we can use sessionStorage
    sessionStorage.setItem('le_flags', flagsJson);
    return true;
  } catch (e) {
    console.error('sessionFlagsRetrieved : Storing flags Error', e);
    return false;
  }
}

function retrieveFlagsJson() {
  try {
    return sessionStorage.getItem('le_flags');
  } catch (e) {
    console.error('sessionFlagsRetrieved : Retrieving flags Error', e);
    return { loaded: false };
  }
}

type FlagDecision = { name: string; decision: any };

// SUBG-1781
// Exported for testing
export function formatFlags(flags: Record<string, any>): FlagDecision[] {
  const formattedFlags: FlagDecision[] = [];
  Object.keys(flags).forEach((key) => {
    const value = flags[key];
    formattedFlags.push({
      name: key,
      decision: value,
    });
  });
  return formattedFlags;
}

// SUBG-1891
// Exported for testing
export function formatContext(context: LDContext): any {
  return context;
}

function trackFlags(flags: Record<string, any>, context: LDContext) {
  try {
    // Track flags to analytics
    track('LaunchDarkly Flags Viewed', {
      le_flags: formatFlags(flags),
      ld_context: formatContext(context),
    });
  } catch (e) {
    console.error('sessionFlagsRetrieved : Track flags Error', e);
    return { loaded: false };
  }
}

// If we have flags and haven't stored them or they have changed then this is
// a new 'Flag Session', we want to send 'LaunchDarkly Flags Viewed' with the
// updated flags values
// If session storage is disabled we DON'T send the event
//
export function sessionFlagsRetrieved(
  flags: LDFlagSet | undefined,
  context: LDContext,
) {
  if (!flags || Object.keys(flags).length === 0) {
    return;
  }

  const newFlagsJson = JSON.stringify(flags);
  const oldFlagsJson = retrieveFlagsJson();

  // Always update the window copy
  // We could have the other analyics use the sessionStored copy
  // but there'd be a bit more JSON overhead and it wouldn't work
  // when session storage is disabled
  //
  // This is actually a proxy object, which may actually update
  // automatically if the flags change in LD, we could convert
  // it to a POJO but maybe we want the updates to be reflected
  window.le_flags = flags;

  if (!storageAvailable()) {
    return;
  }

  // Deep compare window.le_flags to see if there are changes
  if (newFlagsJson === oldFlagsJson) {
    return;
  }

  storeFlagsJson(newFlagsJson);
  trackFlags(flags, context);
}
